import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import MarketplaceSelector from './MarketplaceSelector.jsx';

const ProjectParameters = ({
  projectData,
  onDataChange,
  onNext,
  selectedPrinter,
  selectedFilaments,
  selectedMarketplaces,
  onMarketplaceAdd,
  onMarketplaceUpdate,
  onMarketplaceRemove,
  defaultTransactionItems,
}) => {
  // Defaults inteligentes e preferências do usuário
  useEffect(() => {
    const DEFAULTS = {
      name: '',
      duration: '',
      quantity: 1,
      setupTime: 0,
      setupCostPerHour: Number(localStorage.getItem('prefs_setupCostPerHour')) || 25,
      postProcessTime: 0,
      postProcessCostPerHour: Number(localStorage.getItem('prefs_postProcessCostPerHour')) || 25,
      designHours: 0,
      designCostPerHour: Number(localStorage.getItem('prefs_designCostPerHour')) || 30,
      approvalHours: 0,
      approvalCostPerHour: Number(localStorage.getItem('prefs_approvalCostPerHour')) || 25,
      paintingHours: 0,
      paintingCostPerHour: Number(localStorage.getItem('prefs_paintingCostPerHour')) || 20,
      otherCosts: Number(localStorage.getItem('prefs_otherCosts')) || 0,
      packagingUnitPrice: Number(localStorage.getItem('prefs_packagingUnitPrice')) || 0,
      clientMarkup: Number(localStorage.getItem('prefs_clientMarkup')) || 65,
      vatPercentage: Number(localStorage.getItem('prefs_vatPercentage')) || 0,
      electricityRate: Number(localStorage.getItem('prefs_electricityRate')) || 0.75,
      materialWastePct: Number(localStorage.getItem('prefs_materialWastePct')) || 5,
    };

    Object.entries(DEFAULTS).forEach(([key, val]) => {
      const current = projectData?.[key];
      const isEmpty = current === undefined || current === null || (typeof current === 'string' && current.trim() === '');
      if (isEmpty) {
        onDataChange?.(key, val);
      }
    });
  }, []);

  // Auto-save preferências
  useEffect(() => {
    const savePrefs = (key, value) => {
      if (value !== undefined && value !== null && value !== '') {
        localStorage.setItem(key, String(value));
      }
    };
    savePrefs('prefs_clientMarkup', projectData?.clientMarkup);
    savePrefs('prefs_vatPercentage', projectData?.vatPercentage);
    savePrefs('prefs_setupCostPerHour', projectData?.setupCostPerHour);
    savePrefs('prefs_postProcessCostPerHour', projectData?.postProcessCostPerHour);
    savePrefs('prefs_designCostPerHour', projectData?.designCostPerHour);
    savePrefs('prefs_approvalCostPerHour', projectData?.approvalCostPerHour);
    savePrefs('prefs_paintingCostPerHour', projectData?.paintingCostPerHour);
    savePrefs('prefs_otherCosts', projectData?.otherCosts);
    savePrefs('prefs_packagingUnitPrice', projectData?.packagingUnitPrice);
    savePrefs('prefs_electricityRate', projectData?.electricityRate);
    savePrefs('prefs_materialWastePct', projectData?.materialWastePct);
  }, [projectData]);

  // Outros custos (itens de linha)
  const [otherCostItems, setOtherCostItems] = useState(projectData?.otherCostItems || []);
  useEffect(() => {
    const sum = (otherCostItems || []).reduce((acc, it) => acc + (Number(it?.unitPrice || 0) * Number(it?.quantity || 0)), 0);
    onDataChange?.('otherCosts', sum);
    onDataChange?.('otherCostItems', otherCostItems);
  }, [otherCostItems]);

  const handleInputChange = (field, value) => {
    if (field === 'electricityRate' && value) {
      const numericValue = value?.replace(/[^0-9.,]/g, '')?.replace(',', '.');
      onDataChange(field, numericValue);
      return;
    }
    onDataChange(field, value);
  };

  const validateRequiredFields = () => {
    return projectData?.duration && 
           projectData?.quantity && 
           projectData?.clientMarkup !== undefined;
  };

  // Card padrão para seções
  const SectionCard = ({ children, className = "" }) => (
    <div className={`bg-card border border-border rounded-xl p-6 shadow-sm ${className}`}>
      {children}
    </div>
  );

  // Header de seção padronizado
  const SectionHeader = ({ icon, title, subtitle }) => (
    <div className="flex items-center space-x-3 mb-6">
      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
        <Icon name={icon} size={20} className="text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );

  // Campo de input padronizado
  const FormField = ({ label, children, description }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      {children}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Header Principal */}
      <SectionCard>
        <div className="flex items-center space-x-4 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
            <Icon name="Settings" size={24} className="text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Parâmetros do Projeto</h2>
            <p className="text-muted-foreground">Configure os detalhes técnicos e comerciais do seu projeto</p>
          </div>
        </div>
      </SectionCard>

      {/* 1. Informações Básicas */}
      <SectionCard>
        <SectionHeader 
          icon="ClipboardList" 
          title="Informações Básicas" 
          subtitle="Dados fundamentais do projeto"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Nome do Projeto">
            <Input
              type="text"
              placeholder="Ex: Peça Personalizada Cliente X"
              value={projectData?.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="h-11"
            />
          </FormField>
          
          <FormField label="Tempo de Impressão (horas)" description="Tempo total estimado de impressão">
            <Input
              type="number"
              step="0.1"
              placeholder="8.5"
              value={projectData?.duration || ''}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              className="h-11"
              required
            />
          </FormField>
          
          <FormField label="Quantidade de Peças" description="Número de peças por impressão">
            <Input
              type="number"
              min="1"
              placeholder="1"
              value={projectData?.quantity || ''}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              className="h-11"
              required
            />
          </FormField>
        </div>

        {/* Resumo dos Filamentos */}
        {selectedFilaments && selectedFilaments.length > 0 && (
          <div className="mt-6 p-4 bg-muted/30 rounded-xl border border-border/50">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
              <Icon name="Layers" size={16} className="mr-2 text-primary" />
              Filamentos Selecionados
            </h4>
            <div className="space-y-2">
              {selectedFilaments.map((s, i) => {
                const fd = s?.filamentData || {};
                const weight = parseFloat(s?.weight || 0);
                const waste = parseFloat(s?.waste || 0);
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-foreground font-medium">
                      {fd?.brand} {fd?.material} - {fd?.color}
                    </span>
                    <span className="text-muted-foreground">
                      {weight.toFixed(1)}g + {waste.toFixed(1)}g desperdício
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>

      {/* 2. Mão de Obra */}
      <SectionCard>
        <SectionHeader 
          icon="Clock" 
          title="Mão de Obra" 
          subtitle="Custos de trabalho por categoria"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Preparação */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
              <Icon name="Wrench" size={16} className="mr-2 text-primary" />
              Preparação
            </h4>
            <div className="space-y-3">
              <FormField label="Tempo (horas)">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.setupTime || ''}
                  onChange={(e) => handleInputChange('setupTime', e.target.value)}
                />
              </FormField>
              <FormField label="Custo (R$/hora)">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="25.00"
                  value={projectData?.setupCostPerHour || ''}
                  onChange={(e) => handleInputChange('setupCostPerHour', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Pós-processamento */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
              <Icon name="Scissors" size={16} className="mr-2 text-primary" />
              Pós-processamento
            </h4>
            <div className="space-y-3">
              <FormField label="Tempo (horas)">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.postProcessTime || ''}
                  onChange={(e) => handleInputChange('postProcessTime', e.target.value)}
                />
              </FormField>
              <FormField label="Custo (R$/hora)">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="25.00"
                  value={projectData?.postProcessCostPerHour || ''}
                  onChange={(e) => handleInputChange('postProcessCostPerHour', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Design */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
              <Icon name="PenTool" size={16} className="mr-2 text-primary" />
              Design 3D
            </h4>
            <div className="space-y-3">
              <FormField label="Tempo (horas)">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.designHours || ''}
                  onChange={(e) => handleInputChange('designHours', e.target.value)}
                />
              </FormField>
              <FormField label="Custo (R$/hora)">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="30.00"
                  value={projectData?.designCostPerHour || ''}
                  onChange={(e) => handleInputChange('designCostPerHour', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Aprovação & Comunicação */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
              <Icon name="MessageSquare" size={16} className="mr-2 text-primary" />
              Aprovação & Comunicação
            </h4>
            <div className="space-y-3">
              <FormField label="Tempo (horas)">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.approvalHours || ''}
                  onChange={(e) => handleInputChange('approvalHours', e.target.value)}
                />
              </FormField>
              <FormField label="Custo (R$/hora)">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="25.00"
                  value={projectData?.approvalCostPerHour || ''}
                  onChange={(e) => handleInputChange('approvalCostPerHour', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Pintura/Acabamento */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
              <Icon name="Brush" size={16} className="mr-2 text-primary" />
              Pintura/Acabamento
            </h4>
            <div className="space-y-3">
              <FormField label="Tempo (horas)">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.paintingHours || ''}
                  onChange={(e) => handleInputChange('paintingHours', e.target.value)}
                />
              </FormField>
              <FormField label="Custo (R$/hora)">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="20.00"
                  value={projectData?.paintingCostPerHour || ''}
                  onChange={(e) => handleInputChange('paintingCostPerHour', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Embalagem */}
          <div className="p-4 bg-muted/20 rounded-xl border border-border/30">
            <h4 className="font-medium text-foreground mb-4 flex items-center">
        <Icon name="Filament" size={16} className="mr-2 text-primary" />
              Embalagem
            </h4>
            <div className="space-y-3">
              <FormField label="Preço Unitário (R$)" description="Custo de embalagem por peça">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="2.50"
                  value={projectData?.packagingUnitPrice || ''}
                  onChange={(e) => handleInputChange('packagingUnitPrice', e.target.value)}
                />
              </FormField>
              <div className="p-3 bg-muted/20 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {projectData?.quantity || 1} peças × R$ {(projectData?.packagingUnitPrice || 0).toFixed(2)}
                  </span>
                  <span className="font-semibold text-foreground">
                    R$ {((projectData?.quantity || 1) * (projectData?.packagingUnitPrice || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* 3. Custos Adicionais */}
      <div className="grid grid-cols-1 gap-8">
        {/* Outros Custos */}
        <SectionCard>
          <SectionHeader 
            icon="Plus" 
            title="Outros Custos" 
            subtitle="Insumos e custos extras"
          />
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Itens adicionais</span>
              <Button 
                size="sm" 
                onClick={() => setOtherCostItems(prev => [...prev, { label: '', quantity: 1, unitPrice: 0 }])}
              >
                <Icon name="Plus" size={14} className="mr-1" />
                Adicionar
              </Button>
            </div>
            
            {otherCostItems.length > 0 ? (
              <div className="space-y-3">
              {otherCostItems.map((item, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-3 bg-muted/20 rounded-xl">
                  <Input
                    placeholder="Descrição"
                    value={item.label || ''}
                    onChange={(e) => setOtherCostItems(prev => 
                      prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x)
                      )}
                    className="md:col-span-8"
                  />
                  <Input
                    type="number"
                    placeholder="Qtd"
                    value={item.quantity || 1}
                    onChange={(e) => setOtherCostItems(prev => 
                      prev.map((x, i) => i === idx ? { ...x, quantity: parseInt(e.target.value) || 1 } : x)
                      )}
                    className="md:col-span-2"
                  />
                  <Input
                    type="number"
                    placeholder="R$"
                    value={item.unitPrice || 0}
                    onChange={(e) => setOtherCostItems(prev => 
                      prev.map((x, i) => i === idx ? { ...x, unitPrice: parseFloat(e.target.value) || 0 } : x)
                      )}
                    className="md:col-span-2"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setOtherCostItems(prev => prev.filter((_, i) => i !== idx))}
                    className="md:col-span-12 md:justify-self-end"
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              ))}
                <div className="pt-2 border-t border-border/50">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-semibold text-foreground">
                      R$ {otherCostItems.reduce((acc, it) => acc + (Number(it.unitPrice || 0) * Number(it.quantity || 0)), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhum item adicionado
              </p>
            )}
          </div>
        </SectionCard>

      </div>

      {/* 4. Parâmetros Comerciais */}
      <SectionCard>
        <SectionHeader 
          icon="TrendingUp" 
          title="Parâmetros Comerciais" 
          subtitle="Margem de lucro e impostos"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="space-y-6">
            <FormField label="Margem de Lucro (%)" description="Percentual de lucro sobre os custos">
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="500"
                  step="5"
                  placeholder="65"
                  value={projectData?.clientMarkup || ''}
                  onChange={(e) => handleInputChange('clientMarkup', e.target.value)}
                  className="h-12 text-base px-4 pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-muted-foreground text-sm">%</span>
                </div>
              </div>
            </FormField>
            
            <FormField label="Impostos (%)" description="Percentual de impostos sobre subtotal">
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  placeholder="0"
                  value={projectData?.vatPercentage || ''}
                  onChange={(e) => handleInputChange('vatPercentage', e.target.value)}
                  className="h-12 text-base px-4 pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-muted-foreground text-sm">%</span>
                </div>
              </div>
            </FormField>
          </div>
          
          <div className="space-y-6">
            <h4 className="text-sm font-semibold text-foreground">Configurações Técnicas</h4>
            
            <FormField label="Taxa de Energia (R$/kWh)" description="Custo da energia elétrica">
              <Input
                type="number"
                step="0.01"
                placeholder="0.75"
                value={projectData?.electricityRate || ''}
                onChange={(e) => handleInputChange('electricityRate', e.target.value)}
                className="h-12 text-base px-4"
              />
            </FormField>
            
            <FormField label="Desperdício de Material (%)" description="Percentual de desperdício estimado">
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="20"
                  step="0.1"
                  placeholder="5"
                  value={projectData?.materialWastePct || ''}
                  onChange={(e) => handleInputChange('materialWastePct', e.target.value)}
                  className="h-12 text-base px-4 pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-muted-foreground text-sm">%</span>
                </div>
              </div>
            </FormField>
          </div>
        </div>
      </SectionCard>

      {/* 5. Marketplaces */}
      <SectionCard>
        <SectionHeader 
          icon="ShoppingCart" 
          title="Marketplaces" 
          subtitle="Configuração de vendas em marketplaces"
        />
        <div className="mt-2">
        <MarketplaceSelector
          selectedMarketplaces={selectedMarketplaces}
          onAdd={(item) => {
            const qtyDefault = parseInt(defaultTransactionItems || projectData?.quantity || 1) || 1;
            onMarketplaceAdd?.({ ...item, quantity: qtyDefault });
          }}
          onUpdate={onMarketplaceUpdate}
          onRemove={onMarketplaceRemove}
        />
        </div>
      </SectionCard>

      {/* Botão de Ação */}
      <div className="flex justify-end pt-6">
        <Button
          onClick={onNext}
          disabled={!validateRequiredFields()}
          size="lg"
        >
          <Icon name="ArrowRight" size={18} className="mr-2" />
          Próxima etapa
        </Button>
      </div>
    </motion.div>
  );
};

export default ProjectParameters;
